import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PaperPlaneTilt,
  PencilSimple,
  Plus,
  ShieldCheck,
  ShieldStar,
  Trash,
  UsersThree,
} from '@phosphor-icons/react';

import {
  listAdministrators,
  removeAdministrator,
  resendInvitation,
  revokeInvitation,
  type Administrator,
} from '@/api/admins';
import { listMyOrganizations } from '@/api/orgs';
import { listTeams, type Team } from '@/api/teams';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import {
  TablePagination,
  useTablePaginationState,
} from '@/components/ui/table-pagination';
import {
  SortableHeaderButton,
  useTableSortState,
} from '@/components/ui/table-sort';
import { RowActions, type RowAction } from '@/components/ui/row-actions';
import { useConfirm } from '@/components/ConfirmDialog';
import { AdminFormModal } from '@/components/AdminFormModal';
import { TransferOwnerModal } from '@/components/TransferOwnerModal';

// Administrators page lists every administrator on the org (active + invited)
// in one table. Owner sits at the top and is read-only here — owner transfer
// is a separate flow we'll wire up later. Administrators and leaders can be
// added, edited, and removed; pending invitations get a "Revoke" action.
//
// Authorization: the page itself is reachable for any member of the org (so
// leaders can see who else can act in the org), but the write actions
// (add / edit / remove / revoke) only render when the caller is owner or
// administrator. The backend enforces the same rule.
export function Administrators() {
  const { slug = '' } = useParams();
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Administrator | undefined>(undefined);
  const [transferTarget, setTransferTarget] = useState<Administrator | null>(null);

  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });
  const org = orgsQuery.data?.find((o) => o.slug === slug);
  const callerRole = org?.role ?? '';
  const canManage = callerRole === 'owner' || callerRole === 'administrator';
  const isOwner = callerRole === 'owner';

  const adminsQuery = useQuery({
    queryKey: ['administrators', slug],
    queryFn: () => listAdministrators(token, slug),
    enabled: !!token && !!slug,
  });

  const teamsQuery = useQuery({
    queryKey: ['teams', slug],
    queryFn: () => listTeams(token, slug),
    enabled: !!token && !!slug,
  });
  const teamsByID = useMemo(() => {
    const map = new Map<string, Team>();
    for (const team of teamsQuery.data?.teams ?? []) {
      map.set(team.id, team);
    }
    return map;
  }, [teamsQuery.data]);

  const removeMutation = useMutation({
    mutationFn: (a: Administrator) => {
      if (a.invitation_id) {
        return revokeInvitation(token, slug, a.invitation_id);
      }
      if (a.user_id) {
        return removeAdministrator(token, slug, a.user_id);
      }
      return Promise.resolve();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['administrators', slug] });
    },
  });

  const resendMutation = useMutation({
    mutationFn: (a: Administrator) => {
      if (!a.invitation_id) return Promise.resolve();
      return resendInvitation(token, slug, a.invitation_id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['administrators', slug] });
    },
  });

  const onRemove = async (a: Administrator) => {
    const isInvitation = !!a.invitation_id;
    const ok = await confirm({
      title: t(
        isInvitation
          ? 'settings.administrators.confirm.revokeTitle'
          : 'settings.administrators.confirm.removeTitle',
      ),
      body: t(
        isInvitation
          ? 'settings.administrators.confirm.revokeBody'
          : 'settings.administrators.confirm.removeBody',
        {
          display_name: a.display_name,
          email: a.email,
          org_name: org?.name ?? slug,
        },
      ),
      confirmLabel: t(
        isInvitation
          ? 'settings.administrators.confirm.revokeCta'
          : 'settings.administrators.confirm.removeCta',
      ),
      tone: 'destructive',
    });
    if (!ok) return;
    removeMutation.mutate(a);
  };

  const onResend = async (a: Administrator) => {
    if (!a.invitation_id) return;
    const ok = await confirm({
      title: t('settings.administrators.confirm.resendTitle'),
      body: t('settings.administrators.confirm.resendBody', {
        display_name: a.display_name,
        email: a.email,
      }),
      confirmLabel: t('settings.administrators.confirm.resendCta'),
    });
    if (!ok) return;
    resendMutation.mutate(a);
  };

  const openCreate = () => {
    setEditTarget(undefined);
    setModalOpen(true);
  };
  const openEdit = (a: Administrator) => {
    setEditTarget(a);
    setModalOpen(true);
  };

  const admins = useMemo(() => adminsQuery.data ?? [], [adminsQuery.data]);

  // Sort + pagination both write to the URL. Sort columns map directly to
  // fields on Administrator except `teams` (sorted by the rendered label).
  const sortState = useTableSortState({
    columns: ['name', 'email', 'role', 'teams', 'status'] as const,
  });
  const { page, size, setPage, setSize, offset, limit } = useTablePaginationState({
    defaultSize: 25,
  });

  // Sorted view of the in-memory list. When sortState.sort is null the page
  // shows the server's natural ordering (owner first, then alphabetical) —
  // we only sort when the user has clicked a header.
  const sortedAdmins = useMemo(() => {
    if (!sortState.sort) return admins;
    const factor = sortState.dir === 'asc' ? 1 : -1;
    const valueOf = (a: Administrator): string => {
      switch (sortState.sort) {
        case 'name':
          return a.display_name.toLowerCase();
        case 'email':
          return a.email.toLowerCase();
        case 'role':
          return a.role;
        case 'status':
          return a.status;
        case 'teams':
          // Owners + administrators don't have team scope — sort them as
          // if they covered every team so they cluster at the top.
          if (a.role === 'administrator' || a.role === 'owner') return ' ';
          return a.team_ids.length === 0 ? '￿' : String(a.team_ids.length).padStart(6, '0');
        default:
          return '';
      }
    };
    return [...admins].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av === bv) return 0;
      return av < bv ? -1 * factor : 1 * factor;
    });
  }, [admins, sortState.sort, sortState.dir]);

  // If the data shrinks (e.g. after a removal) such that the current page is
  // past the new last page, snap back to the last valid page.
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(sortedAdmins.length / size));
    if (page > totalPages) setPage(totalPages);
    // setPage is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedAdmins.length, size, page]);

  const pagedAdmins = useMemo(
    () => sortedAdmins.slice(offset, offset + limit),
    [sortedAdmins, offset, limit],
  );

  return (
    <section className="mx-auto w-full max-w-[var(--content-max)] px-4 py-6 sm:px-8 sm:py-8">
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
            {t('settings.administrators.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('settings.administrators.subtitle')}
          </p>
        </div>
        {canManage && (
          <Button onClick={openCreate} className="self-start sm:self-auto sm:shrink-0">
            <Plus className="h-4 w-4" />
            {t('settings.administrators.addButton')}
          </Button>
        )}
      </header>

      {adminsQuery.isLoading && <TableSkeleton columns={6} />}
      {adminsQuery.isError && (
        <p role="alert" className="text-sm text-destructive">
          {t('settings.administrators.loadError')}
        </p>
      )}

      {!adminsQuery.isLoading && !adminsQuery.isError && admins.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border p-8 text-center">
          <p className="font-heading text-lg font-semibold text-foreground">
            {t('settings.administrators.empty.title')}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('settings.administrators.empty.body')}
          </p>
        </div>
      )}

      {admins.length > 0 && (
        <div className="flex flex-col gap-4">
          <div className="overflow-hidden rounded-2xl border border-border bg-background">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <SortableHeaderButton
                      column="name"
                      label={t('settings.administrators.columns.name')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead>
                    <SortableHeaderButton
                      column="email"
                      label={t('settings.administrators.columns.email')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead>
                    <SortableHeaderButton
                      column="role"
                      label={t('settings.administrators.columns.role')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead>
                    <SortableHeaderButton
                      column="teams"
                      label={t('settings.administrators.columns.teams')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead>
                    <SortableHeaderButton
                      column="status"
                      label={t('settings.administrators.columns.status')}
                      state={sortState}
                    />
                  </TableHead>
                  <TableHead className="w-[64px] text-right">
                    <span className="sr-only">
                      {t('settings.administrators.columns.actions')}
                    </span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedAdmins.map((a) => (
                  <AdminTableRow
                    key={a.user_id ?? a.invitation_id ?? a.email}
                    admin={a}
                    teamsByID={teamsByID}
                    canManage={canManage}
                    canTransferOwnership={
                      isOwner && a.status === 'active' && a.role === 'administrator'
                    }
                    onEdit={() => openEdit(a)}
                    onRemove={() => onRemove(a)}
                    onResend={() => onResend(a)}
                    onTransferOwner={() => setTransferTarget(a)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
          <TablePagination
            total={admins.length}
            page={page}
            size={size}
            onPageChange={setPage}
            onSizeChange={setSize}
          />
        </div>
      )}

      <TransferOwnerModal
        open={transferTarget !== null}
        onClose={() => setTransferTarget(null)}
        slug={slug}
        orgName={org?.name ?? slug}
        target={transferTarget}
      />

      <AdminFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        slug={slug}
        {...(editTarget ? { edit: editTarget } : {})}
      />
    </section>
  );
}

function AdminTableRow({
  admin,
  teamsByID,
  canManage,
  canTransferOwnership,
  onEdit,
  onRemove,
  onResend,
  onTransferOwner,
}: {
  admin: Administrator;
  teamsByID: Map<string, Team>;
  canManage: boolean;
  canTransferOwnership: boolean;
  onEdit: () => void;
  onRemove: () => void;
  onResend: () => void;
  onTransferOwner: () => void;
}) {
  const { t } = useTranslation('common');
  const isOwner = admin.role === 'owner';
  const isInvitation = admin.status === 'invited';
  const isLeader = admin.role === 'leader';
  const RoleIcon = isOwner ? ShieldStar : isLeader ? UsersThree : ShieldCheck;

  const teamLabel = useMemo(() => {
    if (admin.role === 'administrator' || isOwner) {
      return t('settings.administrators.teams.all');
    }
    if (admin.team_ids.length === 0) {
      return t('settings.administrators.leader.noTeams');
    }
    const names = admin.team_ids
      .map((id) => teamsByID.get(id)?.name)
      .filter((n): n is string => !!n);
    if (names.length === 0) {
      return t('settings.administrators.teams.count', { count: admin.team_ids.length });
    }
    return names.join(', ');
  }, [admin, isOwner, teamsByID, t]);

  const menuActions: RowAction[] = [];
  if (!isInvitation) {
    menuActions.push({
      id: 'edit',
      label: t('settings.administrators.actions.edit'),
      icon: PencilSimple,
      onSelect: onEdit,
    });
  }
  if (isInvitation) {
    menuActions.push({
      id: 'resend',
      label: t('settings.administrators.actions.resendInvitation'),
      icon: PaperPlaneTilt,
      onSelect: onResend,
    });
  }
  if (canTransferOwnership) {
    menuActions.push({
      id: 'transfer-owner',
      label: t('settings.administrators.actions.transferOwner'),
      icon: ShieldStar,
      onSelect: onTransferOwner,
    });
  }
  menuActions.push({
    id: 'remove',
    label: t(
      isInvitation
        ? 'settings.administrators.actions.revokeInvitation'
        : 'settings.administrators.actions.remove',
    ),
    icon: Trash,
    destructive: true,
    onSelect: onRemove,
  });

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/80"
          >
            <RoleIcon className="h-5 w-5" />
          </span>
          <span className="font-heading text-sm font-semibold text-foreground">
            {admin.display_name}
          </span>
        </div>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{admin.email}</TableCell>
      <TableCell className="text-sm text-foreground">{t(`role.${admin.role}`)}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{teamLabel}</TableCell>
      <TableCell>
        <Badge variant={isInvitation ? 'secondary' : 'outline'}>
          {t(
            isInvitation
              ? 'settings.administrators.status.invited'
              : 'settings.administrators.status.active',
          )}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        {canManage && !isOwner && (
          <RowActions menuActions={menuActions} />
        )}
      </TableCell>
    </TableRow>
  );
}
